import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import "./ReviewModal.css";
import { formatDateISO } from "../../../utils/date";
// import { useNavigate } from 'react-router-dom';
import { Eye, Edit2, Trash2 } from "lucide-react";
import Sidebar from "../side_bar/SidebarAdmin";
import avatar from "../../../assets/avatar.png";
import "./RequestTableCollaborateAdmin.css";
import FlashMessage from "../../../components/common/FlashMessage";
import { useFlashMessage } from "../../../hooks/useFlashMessage";
import {
  listAllCollaborations,
  getCollaborationById,
  updateCollaborationStatus,
  AdminCollaborationDetail,
} from "../../../api/admin/collaborations";
import {
  listImagesByCollaboration,
  updateImageStatus,
} from "../../../api/admin/images";
import {
  listCommentsByCollaboration,
  createComment,
  updateComment as apiUpdateComment,
  deleteComment,
} from "../../../api/admin/comments";
import { useAuth } from "../../../context/AuthContext";
import Header from "../../../components/common/Header";
import TablePagination from "../../../components/common/TablePagination";

interface RequestRow {
  id: number;
  name?: string;
  species?: string;
  country?: string;
  state?: string;
  status: "pending" | "approved" | "rejected" | "correction" | string;
  createdAt?: string;
}

interface DetalhesRequest {
  especie: string;
  pais: string;
  regiao: string;
  estado: string;
  municipio: string;
  latitude: string;
  longitude: string;
  altitude: string;
  dataColeta: string;
  coletor: string;
  instituicaoColetor: string;
  autorFoto: string;
  instituicaoAutorFoto: string;
  sisbio: string;
  sisgen: string;
  observacoes: string;
}

interface Comment {
  id: number; // comentario_id backend
  subject?: string;
  content?: string; // conteudo
  timestamp?: string; // created_at
  author?: { user_id: number; nome_completo?: string } | null;
}

const RequestTableCollaborateAdmin: React.FC = () => {
  // const navigate = useNavigate(); // removido uso direto após integrar imagens inline
  const { message, type, showMessage, clearMessage } = useFlashMessage(5000);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] =
    useState<DetalhesRequest | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [comments, setComments] = useState<Comment[]>([]);
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedRequestStatus, setSelectedRequestStatus] =
    useState<string>("pending");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [images, setImages] = useState<
    { id: number; code: string; url: string; status: string }[]
  >([]);
  const [imagePreview, setImagePreview] = useState<{
    id: number;
    code: string;
    url: string;
    status: string;
  } | null>(null);
  const [currentRequestId, setCurrentRequestId] = useState<number | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<number | null>(null);

  const { getCsrfToken, user } = useAuth();
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [listVersion, setListVersion] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await listAllCollaborations(
          getCsrfToken(),
          selectedStatus ? { status: selectedStatus } : undefined,
          currentPage,
          itemsPerPage
        );
        if (mounted) {
          if (currentPage > Math.max(1, response.totalPages)) {
            setCurrentPage(Math.max(1, response.totalPages));
            return;
          }
          const mappedData = response.items.map((c) => ({
            id: c.colaboracao_id,
            name:
              c.user?.nome_completo || c.user?.user_id
                ? c.user?.nome_completo || `ID ${c.user?.user_id}`
                : "—",
            species: c.nome_especie,
            country: c.pais,
            state: c.estado,
            status: c.status,
            createdAt: c.created_at || c.data,
          }));

          // Ordenar por ID decrescente (mais recentes primeiro)
          const sortedData = mappedData.sort((a, b) => b.id - a.id);

          setRequests(sortedData);
          setTotal(response.total);
          setTotalPages(response.totalPages);
        }
      } catch (e: any) {
        if (mounted) setError(e?.message || "Erro ao carregar");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [getCsrfToken, currentPage, itemsPerPage, selectedStatus, listVersion]);

  const getStatusText = (status: RequestRow["status"]) => {
    const statusMap: Record<string, string> = {
      pending: "Em análise",
      approved: "Aprovada",
      rejected: "Rejeitada",
      correction: "Algo a corrigir",
    };
    return statusMap[status] || status;
  };

  const getStatusClass = (status: RequestRow["status"]) => {
    const statusMap: Record<string, string> = {
      pending: "text-primary",
      approved: "text-success",
      rejected: "text-danger",
      correction: "text-warning",
    };
    return statusMap[status] || "text-muted";
  };

  const handleStatusFilter = (status: string | null) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  const handleViewDetails = async (requestId: number) => {
    setDetailLoading(true);
    setSelectedRequest(null);
    setEditingComment(null);
    setImagePreview(null);
    setComments([]);
    setImages([]);
    clearMessage();
    setCurrentRequestId(requestId);
    try {
      const detail: AdminCollaborationDetail = await getCollaborationById(
        requestId,
        getCsrfToken()
      );
      setSelectedRequest({
        especie: detail.nome_especie || "—",
        pais: detail.pais || "—",
        regiao: detail.regiao || "—",
        estado: detail.estado || "—",
        municipio: detail.municipio || "—",
        latitude: detail.latitude || "—",
        longitude: detail.longitude || "—",
        altitude: detail.altitude || "—",
        dataColeta: formatDateISO(detail.data),
        coletor: detail.coletor || "—",
        instituicaoColetor: detail.instituicao_coletor || "—",
        autorFoto: detail.autor_foto || "—",
        instituicaoAutorFoto: detail.instituicao_autor || "—",
        sisbio: detail.SISBIO || "—",
        sisgen: detail.SISGEN || "—",
        observacoes: detail.observacoes || "—",
      });
      setSelectedRequestStatus(detail.status as any);
      // carregar comentários reais
      const commResponse = await listCommentsByCollaboration(requestId, getCsrfToken()!, 1, 100);
      setComments(
        commResponse.items.map((c: any) => ({
          id: c.comentario_id || c.id,
          subject: c.assunto || c.titulo || c.title || "",
          content: c.conteudo || c.comentario || c.texto || c.text || "",
          timestamp: c.created_at || c.data_criacao || c.data || undefined,
          author: c.autor || c.user || null,
        }))
      );
      // imagens
      const imgsResponse = await listImagesByCollaboration(requestId, getCsrfToken()!, 1, 100);
      // listImagesByCollaboration já normaliza o campo status para frontend (approved/pending/rejected)
      setImages(
        imgsResponse.items.map((i: any) => ({
          id: i.imagem_id,
          code: i.codigo_imagem,
          url: i.url_imagem,
          status: i.status, // já vem normalizado
        }))
      );
    } catch (e) {
      console.error(e);
      showMessage("Não foi possível carregar todos os dados da solicitação.", "error");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAddComment = async () => {
    const subjectInput = document.getElementById("assunto") as HTMLInputElement;
    const commentInput = document.getElementById(
      "comentario"
    ) as HTMLTextAreaElement;
    if (!currentRequestId) return;
    if (commentInput.value) {
      try {
        const created = await createComment(
          currentRequestId,
          {
            assunto: subjectInput.value || undefined,
            conteudo: commentInput.value,
          },
          getCsrfToken()!
        );
        const cAny = created as any;
        const newComment: Comment = {
          id: cAny.comentario_id || cAny.id,
          subject: cAny.assunto || cAny.titulo || cAny.title || "",
          content:
            cAny.conteudo || cAny.comentario || cAny.texto || cAny.text || "",
          timestamp:
            cAny.created_at || cAny.data_criacao || cAny.data || undefined,
          author: cAny.autor || cAny.user || null,
        };
        setComments((prev) => [newComment, ...prev]);
        subjectInput.value = "";
        commentInput.value = "";
        showMessage("Comentário adicionado com sucesso", "success");
      } catch (e: any) {
        showMessage(e?.message || "Erro ao adicionar comentário", "error");
      }
    }
  };

  const handleDeleteCommentClick = (commentId: number) => {
    setCommentToDelete(commentId);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (commentToDelete !== null) {
      try {
        await deleteComment(commentToDelete, getCsrfToken()!);
        setComments((prev) => prev.filter((c) => c.id !== commentToDelete));
        showMessage("Comentário excluído com sucesso", "success");
      } catch (e: any) {
        showMessage(e?.message || "Erro ao excluir comentário", "error");
      }
      setShowDeleteConfirmation(false);
      setCommentToDelete(null);
    }
  };

  // Function to handle comment editing
  const handleEditComment = (comment: Comment) => {
    setEditingComment(comment);
    const subjectInput = document.getElementById("assunto") as HTMLInputElement;
    const commentInput = document.getElementById(
      "comentario"
    ) as HTMLTextAreaElement;

    subjectInput.value = comment.subject || "";
    commentInput.value = comment.content || "";
  };

  // Function to update comment
  const handleUpdateComment = async () => {
    if (editingComment && currentRequestId) {
      const subjectInput = document.getElementById(
        "assunto"
      ) as HTMLInputElement;
      const commentInput = document.getElementById(
        "comentario"
      ) as HTMLTextAreaElement;
      const subject = subjectInput.value;
      const content = commentInput.value;
      try {
        await apiUpdateComment(
          editingComment.id,
          {
            assunto: subjectInput.value || undefined,
            conteudo: commentInput.value,
          },
          getCsrfToken()!
        );
        setComments((prev) =>
          prev.map((c) =>
            c.id === editingComment.id
              ? {
                  ...c,
                  subject,
                  content,
                }
              : c
          )
        );
        showMessage("Comentário atualizado com sucesso", "success");
        setEditingComment(null);
        subjectInput.value = "";
        commentInput.value = "";
      } catch (e: any) {
        showMessage(e?.message || "Erro ao atualizar comentário", "error");
      }
    }
  };

  // Add function to handle status change

  const handleSaveStatus = async () => {
    if (currentRequestId) {
      const subjectInput = document.getElementById(
        "assunto"
      ) as HTMLInputElement | null;
      const commentInput = document.getElementById(
        "comentario"
      ) as HTMLTextAreaElement | null;
      const subject = subjectInput?.value;
      const content = commentInput?.value;
      try {
        // 1. Se estiver editando e houver conteúdo, atualiza comentário antes.
        if (
          editingComment &&
          commentInput?.value.trim() &&
          (commentInput.value !== editingComment.content ||
            subjectInput?.value !== editingComment.subject)
        ) {
          await apiUpdateComment(
            editingComment.id,
            {
              assunto: subjectInput?.value || undefined,
              conteudo: commentInput.value,
            },
            getCsrfToken()!
          );
          setComments((prev) =>
            prev.map((c) =>
              c.id === editingComment.id
                ? {
                    ...c,
                    subject,
                    content,
                  }
                : c
            )
          );
          showMessage("Comentário atualizado com sucesso", "success");
        } else if (
          !editingComment &&
          commentInput?.value.trim() // 2. Se não está editando mas há texto, cria novo
        ) {
          const created = await createComment(
            currentRequestId,
            {
              assunto: subjectInput?.value || undefined,
              conteudo: commentInput.value,
            },
            getCsrfToken()!
          );
          const newComment = {
            id: created.comentario_id,
            subject: created.assunto,
            content: created.conteudo,
            timestamp: created.created_at,
            author: created.autor || null,
          };
          setComments((prev) => [newComment, ...prev]);
          showMessage("Comentário adicionado com sucesso", "success");
        }
        // Limpa campos de comentário depois de salvar
        if (subjectInput) subjectInput.value = "";
        if (commentInput) commentInput.value = "";
        setEditingComment(null);

        // 3. Salva status da colaboração
        await updateCollaborationStatus(
          currentRequestId,
          selectedRequestStatus,
          getCsrfToken()!
        );
        setRequests((prev) =>
          prev.map((r) =>
            r.id === currentRequestId
              ? { ...r, status: selectedRequestStatus }
              : r
          )
        );
        setListVersion((version) => version + 1);
        showMessage("Alterações salvas com sucesso", "success");
        setSelectedRequest(null);
      } catch (e) {
        console.error(e);
        showMessage("Erro ao salvar alterações", "error");
      }
    }
  };
  const handleApproveImage = async (imageId: number) => {
    try {
      const updated = await updateImageStatus(imageId, "approved", getCsrfToken()!);
      setImages((prev) =>
        prev.map((i) =>
          i.id === imageId ? { ...i, status: updated.status } : i
        )
      );
      if (imagePreview && imagePreview.id === imageId)
        setImagePreview((p) => (p ? { ...p, status: updated.status } : p));
    } catch (e: any) {
      showMessage(e?.message || "Erro ao aprovar imagem", "error");
    }
  };
  const handleRejectImage = async (imageId: number) => {
    try {
      const updated = await updateImageStatus(imageId, "rejected", getCsrfToken()!);
      setImages((prev) =>
        prev.map((i) =>
          i.id === imageId ? { ...i, status: updated.status } : i
        )
      );
      if (imagePreview && imagePreview.id === imageId)
        setImagePreview((p) => (p ? { ...p, status: updated.status } : p));
    } catch (e: any) {
      showMessage(e?.message || "Erro ao rejeitar imagem", "error");
    }
  };

  const handleCloseModal = () => {
    setSelectedRequest(null);
    setImagePreview(null);
    setEditingComment(null);
    setShowDeleteConfirmation(false);
    setCommentToDelete(null);
  };

  const runReviewAction = async (action: () => Promise<void>) => {
    if (reviewBusy) return;
    setReviewBusy(true);
    try { await action(); } finally { setReviewBusy(false); }
  };

  const cancelCommentEdit = () => {
    setEditingComment(null);
    const subject = document.getElementById("assunto") as HTMLInputElement | null;
    const content = document.getElementById("comentario") as HTMLTextAreaElement | null;
    if (subject) subject.value = "";
    if (content) content.value = "";
  };

  const renderDeleteConfirmationModal = () => (
    <Modal show={showDeleteConfirmation} onHide={() => { if (!reviewBusy) setShowDeleteConfirmation(false); }}
      centered className="account-area review-modal" aria-labelledby="delete-comment-title" backdrop="static">
      <Modal.Header closeButton={!reviewBusy}><Modal.Title id="delete-comment-title">Excluir comentário</Modal.Title></Modal.Header>
      <Modal.Body>Deseja mesmo excluir este comentário?</Modal.Body>
      <Modal.Footer>
        <button className="btn btn-outline-secondary" disabled={reviewBusy} onClick={() => setShowDeleteConfirmation(false)}>Cancelar</button>
        <button className="btn btn-danger" disabled={reviewBusy} onClick={() => runReviewAction(handleConfirmDelete)}>{reviewBusy ? "Excluindo…" : "Excluir comentário"}</button>
      </Modal.Footer>
    </Modal>
  );

  const renderModal = () => {
    if (!selectedRequest) return null;
    const groups: { title: string; fields: [string, string][] }[] = [
      { title: "Dados da coleta", fields: [["Espécie", selectedRequest.especie], ["Data da coleta", selectedRequest.dataColeta]] },
      { title: "Localização", fields: [["País", selectedRequest.pais], ["Região", selectedRequest.regiao], ["Estado", selectedRequest.estado], ["Município", selectedRequest.municipio], ["Latitude", selectedRequest.latitude], ["Longitude", selectedRequest.longitude], ["Altitude", selectedRequest.altitude]] },
      { title: "Autoria e autorizações", fields: [["Coletor", selectedRequest.coletor], ["Instituição do coletor", selectedRequest.instituicaoColetor], ["Autor da foto", selectedRequest.autorFoto], ["Instituição do autor da foto", selectedRequest.instituicaoAutorFoto], ["SISBIO", selectedRequest.sisbio], ["SISGEN", selectedRequest.sisgen]] },
    ];
    return (
      <>
        <Modal show onHide={() => { if (!reviewBusy) handleCloseModal(); }} size="xl" scrollable
          className="account-area review-modal" aria-labelledby="review-title" backdrop="static"
          enforceFocus={!imagePreview && !showDeleteConfirmation}>
          <Modal.Header closeButton={!reviewBusy}>
            <Modal.Title id="review-title">Solicitação de colaboração #{currentRequestId}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {message && <FlashMessage message={message} type={type} onClose={clearMessage} />}
            {detailLoading && <p role="status">Carregando imagens e comentários…</p>}
            <div className="review-sections">
              {groups.map((group, groupIndex) => (
                <section className="account-panel" key={group.title} aria-labelledby={`review-group-${groupIndex}`}>
                  <div className="account-panel-heading"><h2 id={`review-group-${groupIndex}`}>{group.title}</h2></div>
                  <div className="account-fields">
                    {group.fields.map(([label, value], fieldIndex) => (
                      <div className="account-field" key={label}>
                        <label className="form-label" htmlFor={`review-${groupIndex}-${fieldIndex}`}>{label}</label>
                        <input className="form-control" id={`review-${groupIndex}-${fieldIndex}`} value={value} readOnly />
                      </div>
                    ))}
                  </div>
                </section>
              ))}
              <section className="account-panel" aria-labelledby="review-observations">
                <div className="account-panel-heading"><h2 id="review-observations">Observações</h2></div>
                <textarea className="form-control" aria-labelledby="review-observations" rows={3} value={selectedRequest.observacoes} readOnly />
              </section>
              <section className="account-panel" aria-labelledby="review-images">
                <div className="account-panel-heading"><h2 id="review-images">Imagens ({images.length})</h2></div>
                {!detailLoading && images.length === 0 && <p className="review-empty">Nenhuma imagem disponível.</p>}
                <div className="review-image-grid">
                  {images.map((img) => (
                    <article key={img.id} className="review-image-card">
                      <button type="button" className="review-image-open" aria-label={`Ampliar imagem ${img.code}`} onClick={() => setImagePreview(img)}>
                        <img src={img.url} alt={img.code} loading="lazy" />
                      </button>
                      <div className="review-image-info"><strong>{img.code}</strong><span className={`account-status is-${img.status}`}>{getStatusText(img.status)}</span></div>
                      <div className="review-image-actions">
                        <button type="button" className="btn btn-outline-danger" aria-label={`Rejeitar imagem ${img.code}`} disabled={reviewBusy || detailLoading || img.status === "rejected"} onClick={() => runReviewAction(() => handleRejectImage(img.id))}>Rejeitar</button>
                        <button type="button" className="btn btn-outline-success" aria-label={`Aprovar imagem ${img.code}`} disabled={reviewBusy || detailLoading || img.status === "approved"} onClick={() => runReviewAction(() => handleApproveImage(img.id))}>Aprovar</button>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
              <section className="account-panel" aria-labelledby="review-comments">
                <div className="account-panel-heading"><h2 id="review-comments">Comentários</h2></div>
                <div className="review-comments">
                  {!detailLoading && comments.length === 0 && <p className="review-empty">Sem comentários.</p>}
                  {comments.map((comment) => (
                    <article key={comment.id} className="review-comment">
                      <div>
                        {comment.subject && <h3>{comment.subject}</h3>}
                        <p>{comment.content}</p>
                        <small>{comment.timestamp ? formatDateISO(comment.timestamp) : "—"}{comment.author && ` · ${comment.author.nome_completo || "ID " + comment.author.user_id}`}</small>
                      </div>
                      <div className="review-comment-actions">
                        <button type="button" className="btn btn-outline-primary" aria-label={`Editar comentário ${comment.id}`} disabled={reviewBusy} onClick={() => handleEditComment(comment)}><Edit2 size={16} /></button>
                        <button type="button" className="btn btn-outline-danger" aria-label={`Excluir comentário ${comment.id}`} disabled={reviewBusy} onClick={() => handleDeleteCommentClick(comment.id)}><Trash2 size={16} /></button>
                      </div>
                    </article>
                  ))}
                </div>
                <div className="review-comment-form">
                  <div><label className="form-label" htmlFor="assunto">Assunto</label><input type="text" className="form-control" id="assunto" disabled={reviewBusy || detailLoading} /></div>
                  <div><label className="form-label" htmlFor="comentario">Comentário</label><textarea className="form-control" id="comentario" rows={3} disabled={reviewBusy || detailLoading} /></div>
                  <div className="review-comment-submit">
                    {editingComment && <button type="button" className="btn btn-outline-secondary" disabled={reviewBusy} onClick={cancelCommentEdit}>Cancelar edição</button>}
                    <button type="button" className="btn btn-outline-primary" disabled={reviewBusy || detailLoading} onClick={() => runReviewAction(editingComment ? handleUpdateComment : handleAddComment)}>
                      {editingComment ? "Atualizar comentário" : "Adicionar comentário"}
                    </button>
                  </div>
                </div>
              </section>
              <section className="account-panel review-final-decision" aria-labelledby="review-decision">
                <div className="account-panel-heading"><h2 id="review-decision">Decisão da colaboração</h2></div>
                <p id="review-decision-help" className="review-decision-help"><strong>Aprovar as imagens não aprova a colaboração.</strong> Para concluir a aprovação, selecione <strong>Aprovada</strong> abaixo e confirme no botão <strong>Aprovar colaboração</strong>.</p>
                <div className="review-status-field">
                  <label className="form-label" htmlFor="review-status">Status da colaboração</label>
                  <select id="review-status" className="form-select" aria-describedby="review-decision-help" value={selectedRequestStatus} disabled={reviewBusy || detailLoading} onChange={(event) => setSelectedRequestStatus(event.target.value)}>
                    <option value="pending">Em análise</option><option value="approved">Aprovada</option><option value="rejected">Rejeitada</option><option value="correction">Algo a corrigir</option>
                  </select>
                </div>
              </section>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <p className="review-decision-summary" role="status">{selectedRequestStatus === "pending" ? "A colaboração continuará em análise ao salvar." : selectedRequestStatus === "approved" ? "Confirme para aprovar a colaboração." : selectedRequestStatus === "rejected" ? "Confirme para rejeitar a colaboração." : "Confirme para solicitar correções."}</p>
            <button type="button" className="btn btn-outline-secondary" disabled={reviewBusy} onClick={handleCloseModal}>Fechar</button>
            <button type="button" className="btn btn-primary" disabled={reviewBusy || detailLoading} onClick={() => runReviewAction(handleSaveStatus)}>{reviewBusy ? "Salvando…" : selectedRequestStatus === "approved" ? "Aprovar colaboração" : selectedRequestStatus === "rejected" ? "Rejeitar colaboração" : selectedRequestStatus === "correction" ? "Solicitar correções" : "Salvar decisão"}</button>
          </Modal.Footer>
        </Modal>
        <Modal show={!!imagePreview} onHide={() => { if (!reviewBusy) setImagePreview(null); }} size="xl" centered scrollable className="account-area review-modal" aria-labelledby="review-image-title">
          <Modal.Header closeButton={!reviewBusy}><Modal.Title id="review-image-title">Imagem {imagePreview?.code}</Modal.Title></Modal.Header>
          <Modal.Body>
            {imagePreview && <img src={imagePreview.url} alt={imagePreview.code} className="review-image-large" />}
            {message && <FlashMessage message={message} type={type} onClose={clearMessage} />}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-outline-secondary" disabled={reviewBusy} onClick={() => setImagePreview(null)}>Fechar imagem</button>
            <button type="button" className="btn btn-outline-danger" disabled={reviewBusy || imagePreview?.status === "rejected"} onClick={() => imagePreview && runReviewAction(() => handleRejectImage(imagePreview.id))}>Rejeitar</button>
            <button type="button" className="btn btn-outline-success" disabled={reviewBusy || imagePreview?.status === "approved"} onClick={() => imagePreview && runReviewAction(() => handleApproveImage(imagePreview.id))}>Aprovar</button>
          </Modal.Footer>
        </Modal>
      </>
    );
  };

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredRequests = normalizedQuery
    ? requests.filter((r) => {
        const name = (r.name || "").toLowerCase();
        const species = (r.species || "").toLowerCase();
        return (
          name.includes(normalizedQuery) || species.includes(normalizedQuery)
        );
      })
    : requests;

  const currentRequests = filteredRequests;

  return (
    <div className="account-profile-page account-table-page">
      <Header />
      {message && (
        <div className="container mt-3">
          <FlashMessage message={message} type={type} onClose={clearMessage} />
        </div>
      )}
      {renderDeleteConfirmationModal()}
      {renderModal()}
      <div className="account-profile-layout">
        <div className="account-navigation">
          <Sidebar
            activeItem="solicitacoes-coloboracao"
            avatar={avatar}
            name={user?.nome_completo || "Administrador"}
          />
        </div>
        <main className="account-profile-content">
          <div className="account-page-heading"><h1>Solicitações de colaboração</h1></div>
          <section className="account-table-panel" aria-label="Solicitações de colaboração">
            <div className="account-table-toolbar">
              <label>Pesquisar nesta página
                <input type="search" className="form-control" placeholder="Nome ou espécie" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} />
              </label>
              <label>Status
                <select className="form-select" value={selectedStatus || ""} onChange={(event) => handleStatusFilter(event.target.value || null)}>
                  <option value="">Todos</option><option value="pending">Em análise</option><option value="approved">Aprovada</option><option value="rejected">Rejeitada</option><option value="correction">Algo a corrigir</option>
                </select>
              </label>
            </div>
            <div className="table-responsive" role="region" aria-label="Tabela de solicitações de colaboração" tabIndex={0}>
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>NOME</th>
                    <th>ESPÉCIE</th>
                    <th>PAÍS</th>
                    <th>ESTADO</th>
                    <th>Status</th>
                    <th>AÇÕES</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={7}>Carregando...</td>
                    </tr>
                  )}
                  {error && !loading && (
                    <tr>
                      <td colSpan={7} className="text-danger">
                        {error}
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    !error &&
                    currentRequests.map((request) => (
                      <tr key={request.id}>
                        <td>{request.id}</td>
                        <td>{request.name}</td>
                        <td>{request.species}</td>
                        <td>{request.country}</td>
                        <td>{request.state}</td>
                        <td><span className={`account-status is-${request.status}`}>{getStatusText(request.status)}</span></td>
                        <td>
                          <button
                            className="btn btn-icon"
                            aria-label={`Visualizar solicitação ${request.id}`}
                            title="Visualizar"
                            disabled={detailLoading}
                            onClick={() => handleViewDetails(request.id)}
                          >
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  {!loading && !error && currentRequests.length === 0 && <tr><td colSpan={7} className="account-table-empty">Nenhuma solicitação encontrada nesta página.</td></tr>}
                </tbody>
              </table>
            </div>
            <TablePagination page={currentPage} totalPages={totalPages} pageSize={itemsPerPage} total={total} disabled={loading} onPageChange={setCurrentPage} onPageSizeChange={(size) => { setItemsPerPage(size); setCurrentPage(1); }} />
          </section>
        </main>
      </div>
    </div>
  );
};

export default RequestTableCollaborateAdmin;
